<template>
	<div class="container-fluid">
		<contact-toolbar heading="Invoice/Quote Settings"></contact-toolbar>

		<vue-form v-on:submit="saveSettings" save-button-fixed="true" size="md" :working="working">

			<div class="row">

				<div class="col-lg-6">

					<invoice-display-settings
						:customer-settings="settings" 
						:global-settings="globalInvoiceDisplaySettings"
						layer="customer"
						v-on:settingsChange="displaySettingsChange">
					</invoice-display-settings>

					<h4>Services</h4>
					<field label="Minimum Flat Rate ($)"
						 col-class="col-md-6 col-sm-6"
						 v-model="settings.min_rate"
						 :params="{type: 'number', min:0, step:0.01}">
					</field>

				</div>
				<div class="col-lg-6">
					<h3>Sample Invoice</h3>

					<pdf-js :url="sampleInvoiceUrl" :page="1"></pdf-js>
				</div>
			</div>

			<hr>

			<h3>Invoice Delivery</h3>

			<field label="Invoice Delivery Instructions" 
				col-class="col-sm-4 col-md-4"
				:params="{type:'textarea'}"
				v-model="settings.invoicingInstructions">

				<div class="text-muted">
					Internal instructions on how the customer would like you to deliver the invoice
				</div>

			</field>

			<field label="Requires a hard copy to be sent" 
				col-class="col-sm-6 col-md-4"
				:params="{type:'boolean'}"
				v-model="settings.invoiceHardCopy">
			</field>

			<field label="Default Payment Terms" 
				col-class="col-sm-6 col-md-4"
				:params="{type:'select',options:paymentTermsOptions}"
				v-model="settings.paymentTerms">
			</field>

			<field label="Link to Invoicing Portal" 
				col-class="col-sm-4 col-md-4"
				:params="{type:'text'}"
				v-model="settings.invoicePortalLink">
			</field>

			<field v-if="documentEmailRecipients($store.contact,$store.contact.SubContacts).length > 0" label="Invoice Recipients"
				col-class="col-sm-6 col-md-4"
				:params="{type:'select-multi',options:documentEmailRecipients($store.contact,$store.contact.SubContacts)}"
				v-model="settings.invoiceRecipients">
			</field>

			<alert v-else
				classes="alert-warning"
				:visible="true"
				message="You don't have any emails listed for this Contact, so you won't be able to set a recipient for invoices. Add an email either in the Details tab, or add an email to a Sub-Contact"
				icon="fa-exclamation-triangle">
			</alert>

			<field label="Attach documents directly to email as PDF" 
				col-class="col-sm-4 col-md-4"
				:params="{type:'boolean'}"
				v-model="settings.attachDirectly">

				<alert v-if="settings.attachDirectly"
					classes="alert-warning mt-2"
					:visible="true"
					message="Documents (invoices & POs) that are attached directly to an email as a PDF will lose all open/read tracking capability. Use this setting only if your customer requires it."
					icon="fa-exclamation-triangle">
				</alert>

				<alert v-else
					classes="alert-success mt-2"
					:visible="true"
					message="Documents (invoices & POs) will be delivery via a notice email, which will contain a portal link to download the document. This will enable you to view a log of when a customer has opened and viewed the document."
					icon="fa-check">
				</alert>

			</field>

			<hr>

			<h3>Materials Pricing</h3>

			<div class="row">
				<div class="col-sm-6">
					<field label="Set Custom Price Adjustment"
						col-class="col-md-8"
						:params="{type:'boolean'}"
						v-model="settings.usePriceAdjustment"
						v-on:change="adjustmentToggleChange">
					</field>
				</div>
				<div class="col-sm-6">
					<field v-if="settings.usePriceAdjustment" 
						:params="{type:'number',min:0,step:0.001}"
						v-model="settings.default_price_adjustment">
					</field>
				</div>
			</div>

			<div class="row">
				<div class="col-sm-6">
					<field label="Set Custom Markup"
						col-class="col-md-8"
						:params="{type:'boolean'}"
						v-model="settings.useMarkup"
						v-on:change="markupToggleChange">
					</field>
				</div>
				<div class="col-sm-6">
					<field v-if="settings.useMarkup" 
						:params="{type:'number',min:0,step:0.001}"
						v-model="settings.default_markup">
					</field>
				</div>
			</div>

			<hr>

			<h3>Charge Out Group Over-rides</h3>

			<p>Toggle the checkmark to over-ride the pricing for a specific group</p>

			<div v-for="(group,index) in classificationGroups" >
				
				<field :label="group.label" 
					col-class="col-md-4"
					:params="{type:'boolean'}"
					v-model="chargeOutOverrides[index].override">

					<template v-if="chargeOutOverrides[index] && chargeOutOverrides[index].override">

						<field
							label="Regular Time Rate ($/hr)"
							col-class="col-6 col-sm-4"
							:params="{type:'number',step:0.01}"
							v-model="chargeOutOverrides[index].newRateRegular">
						</field>

						<field
							v-if="timeLogSettings.uses_overtime_15x == 1"
							label="1.5x Overtime Rate ($/hr)"
							col-class="col-6 col-sm-4"
							:params="{type:'number',step:0.01}"
							v-model="chargeOutOverrides[index].newRateOT15x">
						</field>

						<field
							v-if="timeLogSettings.uses_overtime_2x == 1"
							label="2x Overtime Rate ($/hr)"
							col-class="col-6 col-sm-4"
							:params="{type:'number',step:0.01}"
							v-model="chargeOutOverrides[index].newRateOT2x">
						</field>

						<field
							v-if="timeLogSettings.uses_overtime_25x == 1"
							label="2.5x Overtime Rate ($/hr)"
							col-class="col-6 col-sm-4"
							:params="{type:'number',step:0.01}"
							v-model="chargeOutOverrides[index].newRateOT25x">
						</field>

						<hr>
					</template>

				</field>

			</div>


		</vue-form>
	</div>
</template>
<script type="text/javascript">
import ContactToolbar from "./ContactToolbar"
import ContactMixins from "@/mixins/ContactMixins"
import ServiceMixins from "@/mixins/ServiceMixins"
import InvoiceMixins from "@/mixins/InvoiceMixins"

import VueForm from "@/components/VueForm"
import Field from "@/components/Field"
import Alert from "@/components/Alert"
import PdfJs from "@/components/PdfJs"

import InvoiceDisplaySettings from "@/components/InvoiceDisplaySettings"

export default {
	name: "ContactSettings",
	components: {
		ContactToolbar,
		VueForm,
		Field,
		Alert,
		PdfJs,
		InvoiceDisplaySettings
	},
	mixins:[ContactMixins,ServiceMixins,InvoiceMixins],
	data() {
		return {
			settings: {
				default_price_adjustment: "1.000",
				default_markup: "1.000",
				chargeOutGroupOverrides: {},
				lumpSum: false
			},
			globalInvoiceDisplaySettings: null,
			timeLogSettings: {},
			chargeOutOverrides: [],
			classificationGroups: [],
			working: false,
			invoiceVersion: Math.random(),
			companyMinRate: 0
		}
	},
	watch: {
		'$store.company'() {
			console.log("store company change")
			this.setGlobalSettings()
		}
	},
	computed: {
		sampleInvoiceUrl() {
			return window.apiBase+"ServiceInvoice/pdf?slug=sample&token="+this.$store.sessionToken+"&customer_slug="+this.$store.contact.slug+"&v="+this.invoiceVersion
		}
	},
	methods: {
		setGlobalSettings() {
			if (this.$store.company && "CompanySetting" in this.$store.company && this.$store.company.CompanySetting && "job_defaults_json" in this.$store.company.CompanySetting) {
				let settings = {}

				console.log(this.$store.company.CompanySetting.job_defaults_json)

				try {
					settings = JSON.parse(this.$store.company.CompanySetting.job_defaults_json)
				}
				catch (e) {}

				console.log(settings)

				this.globalInvoiceDisplaySettings = settings
			}
		},
		displaySettingsChange(settings) {
			let vm = this
			Object.keys(settings).forEach(key => {
				vm.settings[key] = settings[key]
			})

			this.saveSettings()
		},
		markupToggleChange() {
			let vm = this

			this.$nextTick(() => {
				if (vm.settings.useMarkup == 1) {
					if (!vm.settings.default_markup)
						vm.settings.default_markup = vm.$store.company.CompanySetting.default_markup
				}
			})
		},
		adjustmentToggleChange() {
			let vm = this

			this.$nextTick(() => {
				if (vm.settings.usePriceAdjustment == 1) {
					if (!vm.settings.default_price_adjustment)
						vm.settings.default_price_adjustment = vm.$store.company.CompanySetting.default_price_adjustment
				}
			})
		},
		/*overrideToggle(group) {
			let vm = this

			this.$nextTick(() => {
				if group.override = false
			})
		}*/
		setSettings() {
			let vm = this

			vm.spinner(true)

			let promises = [
				this.getJSON("ClassificationGroup/get"),
				this.getJSON("TimeLogSetting/get")
			]

			let contactFetchIndex = -1

			if (!("id" in this.$store.contact) || this.$store.contact.slug != this.$route.params.id || !("Contact" in this.$store.contact)) {
				promises.push(this.fetchContact(this.$route.params.id,["Contact",{model:"ContactFile", fields:[]}]))
				contactFetchIndex = 2
			}

			else {
				try {
					vm.settings = JSON.parse(vm.$store.contact.settings_json)
				}
				catch (ex) {
					vm.settings = {}
				}

				if (!vm.settings.invoicePortalLink || vm.settings.invoicePortalLink == 'false')
					vm.settings.invoicePortalLink = ""

				if (!vm.settings.invoicingInstructions || vm.settings.invoicingInstructions == 'false')
					vm.settings.invoicingInstructions = ""
			}

			Promise.all(promises).then(responses => {

				vm.spinner(false)

				vm.classificationGroups = responses[0].data

				if (responses[1].data.length > 0)
					vm.timeLogSettings = responses[1].data[0]

				let savedOverrides = []

				if (contactFetchIndex > -1 && responses[contactFetchIndex].success) {

					try {
						vm.settings = JSON.parse(responses[contactFetchIndex].data.settings_json)
					}
					catch (ex) {
						vm.settings = {}
					}

					try {
						savedOverrides = JSON.parse(responses[contactFetchIndex].data.charge_out_group_overrides_json)
					}
					catch (ex) {
						savedOverrides = []
					}
				}

				vm.chargeOutGroupOverrides = []

				//make sure there are values for all chargeout groups
				vm.classificationGroups.forEach(group => {
					let savedOverrideMatch = savedOverrides.find(savedGroup => savedGroup.group_id == group.id)

					if (savedOverrideMatch && savedOverrideMatch.override) {
						vm.chargeOutOverrides.push({
							group_id: savedOverrideMatch.group_id,
							override: savedOverrideMatch.override,
							newRateRegular: savedOverrideMatch.newRateRegular,
							newRateOT15x: savedOverrideMatch.newRateOT15x,
							newRateOT2x: savedOverrideMatch.newRateOT2x,
							newRateOT25x: savedOverrideMatch.newRateOT25x
						})
					}
					else {

						//create the value
						vm.chargeOutOverrides.push({
							group_id: group.id,
							override: false,
							newRateRegular: group.charge_out_rate,
							newRateOT15x: group.ot_charge_out_rate,
							newRateOT2x: group.ot2x_charge_out_rate,
							newRateOT25x: group.ot25x_charge_out_rate
						})
					}
				})

				if (!vm.settings)
					vm.settings = {}

				if (!vm.settings.invoicePortalLink || vm.settings.invoicePortalLink == 'false')
					vm.settings.invoicePortalLink = ""

				if (!vm.settings.invoicingInstructions || vm.settings.invoicingInstructions == 'false')
					vm.settings.invoicingInstructions = ""

				if (!('min_rate' in vm.settings && vm.settings.min_rate) && 'CompanySetting' in vm.$store.company && vm.$store.company.CompanySetting) {
					try {
						const jobDefaults = JSON.parse(vm.$store.company.CompanySetting.job_defaults_json)
						vm.companyMinRate = ('min_rate' in jobDefaults) ? jobDefaults.min_rate : 0
						vm.settings.min_rate = vm.companyMinRate
					} catch {}
				}

			})
			
		},
		saveSettings() {
			let vm = this

			vm.$nextTick(() => {
				vm.working = true

				let settings = vm.cloneObj(vm.settings)

				// Restore to default if min rate is not included, or the same as the company's
				if ('min_rate' in settings) {
					if (settings.min_rate === "" || settings.min_rate == vm.companyMinRate)
						delete settings.min_rate
					else
						this.settings.min_rate = parseFloat(this.settings.min_rate).toFixed(2)
				}

				let payload = {
					id: vm.$store.contact.id,
					settings_json: JSON.stringify(settings),
					charge_out_group_overrides_json: JSON.stringify(vm.chargeOutOverrides)
				}

				vm.postData("Contact/save",payload,true).then(response => {

					if (response.success) {
						vm.alertSaved("Settings saved")
						vm.invoiceVersion = Math.random()
					}

					vm.working = false

				})
			})
		}
	},
	created() {

		this.setGlobalSettings()
		this.setSettings()

	}
}
</script>